import React from 'react';
import { IGlobalStorefrontSettings, IStorefrontLink, IStorefrontSettings, IStorefrontTheme } from '../../../../models/Storefront';
import { Icon, IconButton, Persona, PersonaSize, TooltipHost } from '@fluentui/react';
import StorefrontSearchBarEditor from '../StorefrontSearchBarEditor';
import StorefrontTextEditor from '../StorefrontTextEditor';
import StorefrontHeaderLogoEditor from './StorefrontHeaderLogoEditor';
import StorefrontHeaderLinksEditor from './StorefrontHeaderLinksEditor';
import StorefrontHeaderCustomLinksEditor from './StorefrontHeaderCustomLinksEditor';
import StorefrontColorPickerButton from '../StorefrontColorPickerButton';
import { isDarkColor } from '../colorUtils';
import { resolvedStyleValue, setStyle, styleValue } from '../styleConfig';
import { elementStyleFrom } from '../styleControls';
import { parseHeaderConfig } from '../headerFooterConfig';

interface IStorefrontHeaderEditorProps {
    storefrontSettings: IStorefrontSettings;
    storefrontTheme?: IStorefrontTheme;
    globalSettings: IGlobalStorefrontSettings;
    globalTheme?: IStorefrontTheme;
    editMode?: boolean;
    onStorefrontSettingsChange: (newSettings: IStorefrontSettings) => void;
    /** Opens the header search bar's suggestion-panel style panel, owned by the header/footer editor. */
    onEditSuggestionStyles?: () => void;
    storefrontLinks: IStorefrontLink[];
    globalLinks: IStorefrontLink[];
    onLinksChange: (newLinks: IStorefrontLink[]) => void;
    // When true the bar renders transparent with no shadow so it floats over a full-bleed hero
    // banner sitting behind it (homePageConfig.bannerUnderHeader). backgroundImage (a merchant's
    // scrim gradient) is left intact — only the solid fill + shadow are dropped. editMode is
    // unaffected. Absent/false renders the header exactly as it does today.
    overlayHero?: boolean;
    // Optional overlay-bar chrome (homePageConfig.bannerUnderHeaderBackground / -Shadow): a tint
    // and shadow for the floating bar. Only read when overlayHero is on; unset keeps the bar
    // fully transparent / shadowless.
    overlayBackground?: string;
    overlayShadow?: string;
    // When provided (edit mode), a hover-reveal pencil at the top-right of the header preview opens
    // the header layout panel.
    onEditLayout?: () => void;
}

const StorefrontHeaderEditor: React.FC<IStorefrontHeaderEditorProps> = (props) => {
    const [hoverLayout, setHoverLayout] = React.useState(false);
    const menuBarColor = resolvedStyleValue(props.storefrontSettings, 'menuBarColor') || props.storefrontTheme?.menuBarColor || props.globalSettings.menuBarColor || props.globalTheme?.menuBarColor || '#fff';
    const menuBarTextColor = resolvedStyleValue(props.storefrontSettings, 'menuBarTextColor') || props.storefrontTheme?.menuBarTextColor || props.globalSettings.menuBarTextColor || props.globalTheme?.menuBarTextColor || '#000';
    const headerFont = resolvedStyleValue(props.storefrontSettings, 'headerFont') ?? props.globalSettings.headerFont;
    const isDark = isDarkColor(menuBarColor);
    const headerConfig = React.useMemo(() => parseHeaderConfig(props.storefrontSettings.headerConfig), [props.storefrontSettings.headerConfig]);
    const barStyle = elementStyleFrom(headerConfig.bar);
    const titleStyle = elementStyleFrom(headerConfig.title);
    // Match the live storefront: the title inherits menuBarTextColor from the bar, but an
    // explicit headerConfig.title.color wins. (The storefront applies title.color directly on
    // the title element; without this the editor's inner text hardcodes menuBarTextColor and
    // hides a themed title — e.g. an orange title on a dark bar reads black-on-black here.)
    const titleColor = (titleStyle.color as string | undefined) || menuBarTextColor;
    const logoStyle = elementStyleFrom(headerConfig.logo);
    // The live storefront shows headerConfig.titleText (when set) instead of the storefront name.
    // The inline text editor still edits `name`, so the override is only previewed outside edit mode.
    const showTitleTextOverride = !props.editMode && typeof headerConfig.titleText === 'string';
    // titleText === '' is how the live storefront renders the logo alone ("Show store name next to
    // logo" off, in Header Settings). Outside edit mode the text editor already renders nothing for
    // an empty value; this also drops it in edit mode so the preview matches what shoppers see. The
    // inline name editor goes with it; the name stays editable in the storefront's basic settings.
    const hideTitle = headerConfig.titleText === '';
    const searchBarStyle = elementStyleFrom(headerConfig.searchBar);
    const menuButtonStyle = elementStyleFrom(headerConfig.menuButton);
    const menuStyle = elementStyleFrom(headerConfig.menu);
    const menuHeaderStyle = elementStyleFrom(headerConfig.menuHeader);
    const menuItemStyle = elementStyleFrom(headerConfig.menuItem);
    // Mirror the live storefront dropdown: the popover background is the merchant's `menu` config,
    // else a luminance-aware default (dark on a dark bar); entry text colors are the configured
    // color, else a legible black/white chosen from the popover's luminance.
    const menuBg = (menuStyle.backgroundColor as string | undefined) || (isDark ? 'rgba(40, 42, 54, 0.97)' : '#ffffff');
    const menuPopoverStyle: React.CSSProperties = { backgroundColor: menuBg, border: isDark ? '1px solid rgba(255, 255, 255, 0.2)' : '1px solid rgba(0, 0, 0, 0.12)', ...menuStyle };
    const readableMenuText = isDarkColor(menuBg) ? '#ffffff' : '#1a1a1a';
    const menuItemColor = (menuItemStyle.color as string | undefined) || readableMenuText;
    const menuHeaderColor = (menuHeaderStyle.color as string | undefined) || readableMenuText;
    // Opt-in: headerConfig.brandInline moves the logo+title out of the left-pinned brand slot and
    // into the centered header-links group (mirrors ac-storefront-web NavBar.tsx's brandInline).
    // Absent/false leaves the brand exactly where it renders today (storefront-navbar-left).
    const brandInline = !!headerConfig.brandInline;
    const brandContent = (
        <>
            <StorefrontHeaderLogoEditor
                storefrontSettings={props.storefrontSettings}
                onStorefrontSettingsChange={props.onStorefrontSettingsChange}
                editMode={props.editMode}
                logoStyle={logoStyle}
            />
            {!hideTitle && (
                <span
                    className='storefront-name'
                    style={{
                        fontFamily: headerFont,
                        ...titleStyle,
                    }}
                >
                    <StorefrontTextEditor
                        value={(showTitleTextOverride ? headerConfig.titleText : props.storefrontSettings.name)?.trim()}
                        notSetValue={props.globalSettings.name?.trim() || 'Storefront Name'}
                        onChange={(name) => props.onStorefrontSettingsChange({ ...props.storefrontSettings, name })}
                        editable={props.editMode}
                        rootClassName='storefront-inline'
                        style={{
                            fontFamily: headerFont,
                            color: titleColor,
                        }}
                    />
                </span>
            )}
        </>
    );
    return (
        <div
            className='storefront-navbar-wrapper'
            style={{ position: 'relative' }}
            onMouseEnter={() => setHoverLayout(true)}
            onMouseLeave={() => setHoverLayout(false)}
        >
        {props.editMode && props.onEditLayout && (
            <TooltipHost content='Edit header layout'>
                <IconButton
                    iconProps={{ iconName: 'Edit' }}
                    ariaLabel='Edit header layout'
                    onClick={props.onEditLayout}
                    styles={{ root: { position: 'absolute', top: 6, right: 6, zIndex: 11, opacity: hoverLayout ? 1 : 0, transition: 'opacity .15s', background: 'rgba(255,255,255,0.92)', borderRadius: 4, boxShadow: '0 1px 3px rgba(0,0,0,0.25)' } }}
                />
            </TooltipHost>
        )}
        <div
            className='storefront-navbar'
            style={{
                backgroundColor: menuBarColor,
                color: menuBarTextColor,
                ...barStyle,
                // Overlay mode floats the bar over the hero banner: replace the solid fill + shadow
                // AFTER barStyle so they win (with the configured overlay tint/shadow, defaulting to
                // fully transparent), but keep any backgroundImage (merchant scrim gradient).
                ...(props.overlayHero ? { backgroundColor: props.overlayBackground ?? 'transparent', boxShadow: props.overlayShadow ?? 'none' } : {}),
            }}
        >
            <div className='storefront-navbar-left'>
                {!brandInline && brandContent}
            </div>
            {brandInline ? (
                // Centered group: brand first, then the custom header links — mirrors NavBar.tsx's
                // .header-center rendering the brand inline when there's a centered slot to join.
                // Plain wrapper (NOT .storefront-header-links-slot — CustomLinksEditor already
                // renders its OWN .storefront-header-links-slot, which is flex:1 1 auto): this takes
                // the middle space and centers a tight group, and the inner CustomLinksEditor is
                // boxed at flex:0 0 auto so its own slot's grow doesn't shove the brand left of center.
                <div style={{ flex: '1 1 auto', minWidth: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '1.5em' }}>
                    <div style={{ display: 'flex', alignItems: 'center', flex: '0 0 auto' }}>
                        {brandContent}
                    </div>
                    <div style={{ flex: '0 0 auto', display: 'flex', alignItems: 'center' }}>
                        <StorefrontHeaderCustomLinksEditor
                            storefrontSettings={props.storefrontSettings}
                            onStorefrontSettingsChange={props.onStorefrontSettingsChange}
                            editMode={props.editMode}
                        />
                    </div>
                </div>
            ) : (
                <StorefrontHeaderCustomLinksEditor
                    storefrontSettings={props.storefrontSettings}
                    onStorefrontSettingsChange={props.onStorefrontSettingsChange}
                    editMode={props.editMode}
                />
            )}
            <div className='storefront-navbar-right'>
                {/* margin mirrors the live .home-search-bar's `margin: 1em` (vertical part) — it's what
                    makes a search-enabled store's default bar ~76px tall, so the preview must carry it
                    too for the heights to match. */}
                {props.storefrontSettings.useHeaderSearch && <div style={{ margin: '1em 0' }}><StorefrontSearchBarEditor context='header' onEditStyles={props.onEditSuggestionStyles} onStorefrontSettingsChange={props.onStorefrontSettingsChange} storefrontSettings={props.storefrontSettings} editMode={props.editMode} isDark={isDark} style={searchBarStyle} /></div>}
                <StorefrontHeaderLinksEditor
                    storefrontSettings={props.storefrontSettings}
                    storefrontLinks={props.storefrontLinks}
                    globalLinks={props.globalLinks}
                    onLinksChange={props.onLinksChange}
                    storefrontTheme={props.storefrontTheme}
                    globalSettings={props.globalSettings}
                    globalTheme={props.globalTheme}
                    editMode={props.editMode}
                    onStorefrontSettingsChange={props.onStorefrontSettingsChange}
                    isDark={isDark}
                    menuIsDark={isDarkColor(menuBg)}
                    buttonStyle={menuButtonStyle}
                    menuHeaderStyle={menuHeaderStyle}
                    menuItemStyle={menuItemStyle}
                    menuPopoverStyle={menuPopoverStyle}
                    menuItemColor={menuItemColor}
                    menuHeaderColor={menuHeaderColor}
                />
                <Icon iconName='ShoppingCart' style={{ fontSize: 24 }} />
                <Persona text='Adaptive Catalog' size={PersonaSize.size32} hidePersonaDetails />
            </div>
        </div>
        {props.editMode && (
            <StorefrontColorPickerButton
                label='Header Bar'
                entries={[
                    {
                        type: 'color',
                        label: 'Bar Color',
                        settingKey: 'storefront.header.menuBarColor',
                        allowPaletteTokens: true,
                        value: styleValue(props.storefrontSettings, 'menuBarColor'),
                        inheritedValue: props.storefrontTheme?.menuBarColor || props.globalSettings.menuBarColor || props.globalTheme?.menuBarColor,
                        onChange: (color) => props.onStorefrontSettingsChange(setStyle(props.storefrontSettings, { menuBarColor: color })),
                        onClear: () => props.onStorefrontSettingsChange(setStyle(props.storefrontSettings, { menuBarColor: '' })),
                    },
                    {
                        type: 'color',
                        label: 'Text Color',
                        settingKey: 'storefront.header.menuBarTextColor',
                        allowPaletteTokens: true,
                        value: styleValue(props.storefrontSettings, 'menuBarTextColor'),
                        inheritedValue: props.storefrontTheme?.menuBarTextColor || props.globalSettings.menuBarTextColor || props.globalTheme?.menuBarTextColor,
                        onChange: (color) => props.onStorefrontSettingsChange(setStyle(props.storefrontSettings, { menuBarTextColor: color })),
                        onClear: () => props.onStorefrontSettingsChange(setStyle(props.storefrontSettings, { menuBarTextColor: '' })),
                    },
                    {
                        type: 'font',
                        label: 'Font',
                        settingKey: 'storefront.style.headerFont',
                        allowPaletteTokens: true,
                        value: styleValue(props.storefrontSettings, 'headerFont'),
                        inheritedValue: props.globalSettings.headerFont,
                        onChange: (font) => props.onStorefrontSettingsChange(setStyle(props.storefrontSettings, { headerFont: font })),
                        onClear: () => props.onStorefrontSettingsChange(setStyle(props.storefrontSettings, { headerFont: '' })),
                    },
                ]}
                style={{ position: 'absolute', bottom: -14, left: '50%', transform: 'translateX(-50%)', zIndex: 10 }}
            />
        )}
        </div>
    )
}

export default StorefrontHeaderEditor;